import { ThunkAction } from 'redux-thunk';
import { Action, ActionCreator, Reducer } from 'redux';
import { checkScenario } from '@mfe/util/scenario';
import CustomError from '../classes/CustomError';
import { getTracingHeaders } from '../util/helpers';
import type { RootState } from './index';

// action types

export const VALIDATE = 'auth/VALIDATE';
export const PROCESSING = 'auth/PROCESSING';
export const ERROR = 'auth/ERROR';

// action creators

export const validate: ActionCreator<ThunkAction<Promise<Action>, RootState, void, Action>> = () => async (
  dispatch,
  getState,
): Promise<Action> => {
  try {
    dispatch({ type: PROCESSING });
    const { config } = getState();
    const {
      apiUri,
      accessToken: configAccessToken,
      customerToken,
      registerToken,
      traceid,
    } = config;

    const { isRaittNoTokensWithInterception, isAuthOverride } = checkScenario(config);

    const headers: Record<string, string> = {
      Accept: 'application/json; charset=utf-8',
      ...getTracingHeaders(traceid),
    };

    if (isAuthOverride) {
      headers['x-authorization-override'] = customerToken
        ? `Customer ${customerToken}`
        : `Register ${registerToken}`;
    }

    let path;
    if (isRaittNoTokensWithInterception) {
      path = '/identity';
    } else {
      path = registerToken
        ? '/token'
        : '/userprofile';
      headers.Authorization = configAccessToken
        ? `Bearer ${configAccessToken}`
        : `Register ${registerToken}`;
    }

    const response = await fetch(`${apiUri}${path}`, {
      method: 'GET',
      mode: 'cors',
      credentials: 'same-origin',
      headers,
    });
    const body = await response.json() as unknown;
    if (!response.ok) {
      const { error: statusText, message, statusCode } = body as {
        error: string;
        message: string;
        statusCode: number;
      };
      throw new CustomError({ statusText, message, statusCode });
    }
    const {
      registerId,
      lcId,
      spId,
      accessToken, // coms internal access token
      username = 'unknown',
    } = body as {
      registerId?: string;
      lcId?: string;
      spId?: string;
      accessToken?: string;
      username: string;
    };
    return dispatch({
      type: VALIDATE,
      registerId,
      lcId,
      spId,
      accessToken,
      username,
    });
  } catch (error) {
    dispatch({ type: ERROR, error });
    throw error;
  }
};

export type AuthState = {
  accessToken?: string;
  spId?: string;
  username?: string;
  lcId?: string;
  registerId?: string;
  processing: boolean;
  error: boolean;
};

const initial: AuthState = {
  processing: false,
  error: false,
};

type AuthAction = Action<typeof VALIDATE | typeof PROCESSING | typeof ERROR> & AuthState;

// reducer

export const reducer: Reducer<AuthState, AuthAction> = (
  state = initial,
  action = {
    type: undefined,
    ...initial,
  },
) => {
  const {
    type,
    registerId,
    lcId,
    spId,
    accessToken,
    username,
    error,
  } = action;
  switch (type) {
    case VALIDATE:
      return {
        registerId,
        lcId,
        spId,
        accessToken,
        username,
        processing: false,
        error: false,
      };
    case PROCESSING:
      return { ...state, processing: true, error: false };
    case ERROR:
      return { ...state, processing: false, error };
    default:
      return state;
  }
};
